import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { BillingRun, BillingRunReport } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { useBillingRuns, useRunBilling } from '../../lib/api/hooks-subscriptions.js';
import { Badge, Empty, ErrorBanner, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatMoney } from '../../lib/format.js';
import { docDate } from './InvoiceDocument.js';

/**
 * Billing runs (docs/28 B3): each direct client's period, drafted as an invoice on
 * its billing day, for staff to review and issue in Invoices. From an admin
 * address only (nginx).
 */

const summary = (r: BillingRunReport): string => {
  const parts = [
    r.drafted > 0 && `${r.drafted} drafted`,
    r.nothing_to_bill > 0 && `${r.nothing_to_bill} with nothing to bill`,
    r.already_run > 0 && `${r.already_run} already run`,
    r.failed > 0 && `${r.failed} failed`,
  ].filter(Boolean);
  return parts.length > 0 ? `${parts.join(', ')}.` : 'No accounts to run.';
};

function RunStatus({ run }: { run: BillingRun }) {
  if (run.status === 'failed') return <Badge tone="danger">failed</Badge>;
  if (run.status === 'nothing_to_bill') return <Badge tone="muted">nothing to bill</Badge>;
  if (!run.invoice) return <Badge tone="muted">draft deleted</Badge>;
  return run.invoice.status === 'draft' ? <Badge tone="warn">draft</Badge> : <Badge tone="ok">issued</Badge>;
}

export function BillingRunsPage() {
  const can = useSession((s) => s.can);
  const runs = useBillingRuns();
  const run = useRunBilling();
  const step = useStepUp();
  const [report, setReport] = useState<BillingRunReport | null>(null);
  /** The account being run again, or null for a run of everything due. */
  const [running, setRunning] = useState<string | null>(null);

  const start = (body: { tenant_id?: string; rerun?: boolean }) => {
    setReport(null);
    setRunning(body.tenant_id ?? null);
    void step.run(() => run.mutateAsync(body).then(setReport))
      .catch(() => undefined)
      .finally(() => setRunning(null));
  };

  const canRun = can('invoice:issue');
  const data = runs.data;

  // Only an account's latest run can be run again: it is the period now due.
  const latest = new Map<string, string>();
  for (const r of data?.data ?? []) {
    if (r.tenant_id && !latest.has(r.tenant_id)) latest.set(r.tenant_id, r.id);
  }

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Billing runs</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Each direct client&apos;s period, drafted as an invoice on its billing day
          </p>
        </div>
        {canRun && (
          <button type="button" className="arc-btn arc-btn-primary" disabled={run.isPending}
            onClick={() => start({})}>
            {run.isPending && running === null ? 'Running…' : 'Run what is due'}
          </button>
        )}
      </div>

      <StepUpPrompt step={step} what="Running billing" />
      {report && <p className="arc-muted" style={{ margin: '0 0 12px' }}>{summary(report)}</p>}
      {report && report.errors.length > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} />
          <div>{report.errors.map((e) => <div key={e}>{e}</div>)}</div>
        </div>
      )}
      {data && data.untyped_seats.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }}>
          <Icon name="warning" size={14} />
          <div>
            Seats with no extension type are not billed:{' '}
            {data.untyped_seats.map((u) => `${u.account_name} (${u.seats})`).join(', ')}.
          </div>
        </div>
      )}

      <div className="arc-card">
        {runs.error ? <div className="arc-card-body"><ErrorBanner error={runs.error} /></div> : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Account</th><th>Period</th><th>Status</th><th>Invoice</th>
                  <th className="arc-td-right">Total</th>
                  <th />
                </tr>
              </thead>
              {runs.data === undefined ? <TableSkeleton cols={6} /> : (
                <tbody>
                  {data?.data.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div>{r.account_name}</div>
                        {[...(r.error ? [r.error] : []), ...r.warnings].map((note) => (
                          <div key={note} className="arc-subtle" style={{ fontSize: 12.5 }}>{note}</div>
                        ))}
                      </td>
                      <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                        {docDate(r.period_start)} to {docDate(r.period_end)}
                      </td>
                      <td><RunStatus run={r} /></td>
                      <td className="arc-mono">
                        {r.invoice?.number
                          ? <Link to="/billing/invoices/$" params={{ _splat: r.invoice.number }}>{r.invoice.number}</Link>
                          : r.invoice ? <Link to="/billing/invoices">In drafts</Link> : '—'}
                      </td>
                      <td className="arc-td-right arc-num">
                        {r.invoice ? formatMoney(r.invoice.total, r.currency) : '—'}
                      </td>
                      <td className="arc-td-right">
                        {canRun && r.tenant_id && latest.get(r.tenant_id) === r.id
                          && (r.status !== 'drafted' || !r.invoice || r.invoice.status === 'draft') && (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            disabled={run.isPending}
                            onClick={() => start({ tenant_id: r.tenant_id!, rerun: true })}>
                            {run.isPending && running === r.tenant_id ? 'Running…' : 'Run again'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        )}
        {runs.data !== undefined && data?.data.length === 0 && (
          <Empty icon="billing" title="No runs yet" hint="A client's period is drafted on its billing day." />
        )}
      </div>
    </>
  );
}
